import { useMemo } from 'react';
// Dialogue IN-APP à la palette de l'IDE — CCT-100.
//
// window.confirm / alert cassent l'immersion (chrome du navigateur, pas thémé, une seule
// question à la fois). Ce composant reprend le style CYB (fond dark, liseré cyan, boutons
// plats) et sait présenter PLUSIEURS options à cocher dans une même boîte — c'est ce que
// demande la sauvegarde d'une template (réinitialiser + rembobiner).
import React from 'react';
import { Modal, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import { type Palette } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import { useT } from '../i18n/useT';

export interface CybOption {
  key: string;
  label: string;
  /** Précision sous le libellé : ce que l'option COÛTE, pour décider en connaissance. */
  detail?: string;
  default?: boolean;
}

export interface CybDialogProps {
  visible: boolean;
  title: string;
  /** Message principal ; un `warning` le colore en rouge (impact grave). */
  message?: string;
  warning?: boolean;
  options?: CybOption[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** Le bouton de confirmation vire au rouge (action destructrice). */
  destructive?: boolean;
  busy?: boolean;
  onCancel: () => void;
  /** Reçoit l'état des cases (clé → coché) ; objet vide s'il n'y a pas d'options. */
  onConfirm: (checked: Record<string, boolean>) => void;
}

export default function CybDialog({
  visible, title, message, warning, options = [], confirmLabel,
  cancelLabel, destructive, busy, onCancel, onConfirm,
}: CybDialogProps) {
  // ⚠️ THE DEFAULTS CANNOT LIVE IN THE SIGNATURE. A parameter default is evaluated before the
  // component body runs, so no hook is available there — a translated string would have to be a
  // literal, frozen in one language. They are resolved here instead.
  const t = useT();
  const confirmText = confirmLabel ?? t('common.confirm');
  const cancelText = cancelLabel ?? t('common.cancel');
  const C = usePalette();
  const st = useMemo(() => makeStyles(C), [C]);
  const [checked, setChecked] = React.useState<Record<string, boolean>>({});

  // Réinitialise aux défauts à CHAQUE ouverture : une boîte rouverte ne doit pas retenir les
  // choix de la fois d'avant.
  React.useEffect(() => {
    if (visible) {
      setChecked(Object.fromEntries(options.map(o => [o.key, !!o.default])));
    }
  }, [visible]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={st.backdrop} onPress={onCancel}>
        <Pressable style={st.box} onPress={() => {}}>
          <View style={st.head}>
            <Text style={st.title}>{title}</Text>
            <TouchableOpacity onPress={onCancel} style={st.close}>
              <Ionicons name="close" size={18} color={C.textSec} />
            </TouchableOpacity>
          </View>

          {!!message && (
            <Text style={[st.message, warning && { color: C.red }]}>{message}</Text>
          )}

          {options.map(o => {
            const on = !!checked[o.key];
            return (
              <TouchableOpacity key={o.key} activeOpacity={0.7}
                onPress={() => setChecked(c => ({ ...c, [o.key]: !c[o.key] }))}
                style={[st.opt, on && st.optOn]}>
                <View style={[st.checkbox, on && st.checkboxOn]}>
                  {on && <Ionicons name="checkmark" size={12} color={C.bg} />}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={st.optLabel}>{o.label}</Text>
                  {!!o.detail && <Text style={st.optDetail}>{o.detail}</Text>}
                </View>
              </TouchableOpacity>
            );
          })}

          <View style={st.actions}>
            <TouchableOpacity onPress={onCancel} disabled={busy} style={[st.btn, st.btnGhost]}>
              <Text style={st.btnGhostText}>{cancelText}</Text>
            </TouchableOpacity>
            <TouchableOpacity disabled={busy}
              onPress={() => onConfirm(checked)}
              style={[st.btn, destructive ? st.btnDanger : st.btnPrimary]}>
              <Text style={[st.btnText, destructive && { color: C.red }]}>{confirmText}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const makeStyles = (C: Palette) => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', padding: 20 },
  box: { width: '100%', maxWidth: 460, backgroundColor: C.surface, borderWidth: 1,
         borderColor: C.borderCy, borderRadius: 14, padding: 18, gap: 12 },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  title: { flex: 1, color: C.textPrim, fontSize: 16, fontWeight: '700' },
  close: { padding: 2 },
  message: { color: C.textSec, fontSize: 13, lineHeight: 19 },
  opt: { flexDirection: 'row', gap: 10, alignItems: 'flex-start', borderWidth: 1,
         borderColor: C.border, borderRadius: 10, padding: 11, backgroundColor: C.card },
  optOn: { borderColor: C.borderCy, backgroundColor: 'rgba(0,240,255,0.06)' },
  checkbox: { width: 20, height: 20, borderRadius: 5, borderWidth: 1.5, borderColor: C.textMute,
              alignItems: 'center', justifyContent: 'center' },
  checkboxOn: { backgroundColor: C.cyan, borderColor: C.cyan },
  optLabel: { color: C.textPrim, fontSize: 13, fontWeight: '600' },
  optDetail: { color: C.textSec, fontSize: 11, lineHeight: 16, marginTop: 2 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 4 },
  btn: { paddingHorizontal: 16, paddingVertical: 9, borderRadius: 8, borderWidth: 1 },
  btnGhost: { borderColor: C.border, backgroundColor: 'transparent' },
  btnGhostText: { color: C.textSec, fontSize: 13, fontWeight: '600' },
  btnPrimary: { borderColor: C.borderCy, backgroundColor: 'rgba(0,240,255,0.12)' },
  btnDanger: { borderColor: C.red, backgroundColor: 'rgba(255,45,85,0.10)' },
  btnText: { color: C.cyan, fontSize: 13, fontWeight: '700' },
});
